import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
import { TEXT_DETAIL, creaseNormals, loopArea, loopsToShapes, simplifyLoop, traceIso } from '../core/text-outline.js';
import { $, clamp } from '../state/store.js';
import { fontSpec } from '../core/fonts.js';


/* ── analytic ambient occlusion ─────────────────────────────
   Inside a recess the only opening is the mouth, so the ambient light reaching
   any point is exactly the cosine-weighted form factor from that point to the
   mouth rectangle. Lambert's closed form gives it directly — no ray marching,
   no screen-space pass, and it's correct rather than approximate.
     F = (1/2π) · Σ_edges  γ_i · (n · m̂_i)
   where γ_i is the angle subtended by edge i and m̂_i its normalised
   cross-product. Baked into vertex colours. */
function formFactorToRect(p, n, rect){
  let sum = 0;
  for(let i=0;i<4;i++){
    const q = rect[i], r = rect[(i+1)%4];
    const A=[q[0]-p[0],q[1]-p[1],q[2]-p[2]], B=[r[0]-p[0],r[1]-p[1],r[2]-p[2]];
    const la=Math.hypot(A[0],A[1],A[2]), lb=Math.hypot(B[0],B[1],B[2]);
    if(la<1e-12||lb<1e-12) continue;
    const a=[A[0]/la,A[1]/la,A[2]/la], b=[B[0]/lb,B[1]/lb,B[2]/lb];
    const g = Math.acos(clamp(a[0]*b[0]+a[1]*b[1]+a[2]*b[2], -1, 1));
    const c = [a[1]*b[2]-a[2]*b[1], a[2]*b[0]-a[0]*b[2], a[0]*b[1]-a[1]*b[0]];
    const lc = Math.hypot(c[0],c[1],c[2]);
    if(lc<1e-12) continue;
    sum += g * (n[0]*c[0]+n[1]*c[1]+n[2]*c[2])/lc;
  }
  return clamp(sum/(2*Math.PI), 0, 1);
}

/* A recess: an opening at the screen plane cut back into the wall behind it.

   Rings, front to back —
     R0            outer edge of the flat border, z = 0
     bevel rings   a quarter-round from the border plane to the wall
     R3            back of the side walls, z = -depth   (+ a back face)

   Every normal here is supplied analytically rather than derived from the
   winding. The bevel is a known quarter-round, so its exact surface normal
   sweeps from +Z (tangent to the border) to the wall's inward axis (tangent to
   the wall). That gives smooth shading across the fillet with true tangency at
   both ends, while border, walls and back face keep bit-exact flat normals —
   no crease-angle heuristic, and nothing to leak into a flat surface. */
/* The frame can be thicker on one side than another — a deeper top border, say.
   `border` stays the single linked value; the four overrides only apply once the
   link is off, and fall back to it when they are null. */
export function recessBorders(p){
  const b = Math.max(0, p.border || 0);
  if(p.linkBorder === false){
    const pick = v => Math.max(0, v == null ? b : v);
    return { top:pick(p.borderT), right:pick(p.borderR), bottom:pick(p.borderB), left:pick(p.borderL) };
  }
  return { top:b, right:b, bottom:b, left:b };
}

/* ONLY BORDERS — p.borderOverlay.
 *
 * Normally the border and the opening are one quantity: auto-fill sizes the
 * hole as SCREEN MINUS BORDER, so thickening the frame eats into the recess,
 * and on a corner it eats into the depth as well — cornerL/cornerR ARE the
 * depths there, so a fatter frame quietly makes the notch shallower. That
 * coupling is right for a recess cut into a wall, and wrong for a frame stuck
 * on the front of one.
 *
 * In overlay mode the interior is built as if the border were ZERO — full
 * screen, full depth — and the border is drawn INWARD over that opening
 * instead of OUTWARD beside it. The visible hole is the same rectangle either
 * way; what changes is what is behind it. Thicken the frame here and the room
 * does not move at all.
 *
 * The structure survives unchanged because both modes are the same four rings
 * — outer, front-face lip, bevel, wall — with two of them re-anchored. Every
 * expression below reduces to exactly what it was when borderOverlay is off,
 * which is why old projects are bit-identical rather than merely close. */
function buildRecessGeometry(p){
  const w = Math.max(.01, p.w), h = Math.max(.01, p.h);
  const depth = Math.max(.001, p.depth);
  const ovl = p.borderOverlay === true;
  const B0 = recessBorders(p);
  /* Overlaid, a border is spent out of the opening rather than added around it,
     so it is the one mode where a border can be too big to draw. Clamped per
     side rather than as a sum, so one runaway value cannot collapse the side
     opposite it. */
  const B = ovl
    ? { top:    Math.min(B0.top,    h/2*.98), right:  Math.min(B0.right,  w/2*.98),
        bottom: Math.min(B0.bottom, h/2*.98), left:   Math.min(B0.left,   w/2*.98) }
    : B0;
  /* BORDER DEPTH — how thick the frame is, overlaid only.
     Null falls back to the bevel, the same idiom borderT/R/B/L use against
     border: leave it alone and the roll is the whole thickness, exactly as an
     unset value always rendered. Set it and the frame becomes a moulding — the
     roll over its front edge, then a straight skirt down to this depth, which
     is the difference between a rounded lip and a band with real weight.
     Framed, there is no such quantity to set: the border IS the wall around the
     mouth, and the material behind it runs the full depth of the recess. */
  const fd = ovl
    ? Math.min(p.borderDepth == null ? Math.max(0, p.bevel||0) : Math.max(0, p.borderDepth), depth*.98)
    : 0;
  /* Overlaid, the fillet also rolls inward off the frame's edge, so it has the
     VISIBLE opening to fit inside — not the whole recess — and it cannot be
     deeper than the frame it is rolling over. Without those terms a bevel wider
     than the remaining gap turned the lip inside out. */
  const bev = clamp(p.bevel||0, 0, ovl
    ? Math.min(depth*.98, Math.min(w,h)*.49, fd,
               Math.min(w - (B.left+B.right), h - (B.top+B.bottom))*.49)
    : Math.min(depth*.98, Math.min(w,h)*.49));
  const bor = Math.max(B.top, B.right, B.bottom, B.left);   // widest side, for tolerances
  const aoStr = 0;   // baked occlusion removed entirely; scene lighting does all the shading
  const bsegs = 5;   // bevel shape is no longer a user choice — always the "very rounded" segment count
  const hx = w/2, hy = h/2;

  /* Keep the OUTER rectangle centred on the object's origin and slide the opening
     instead. That way an uneven frame does not move the piece relative to the
     screen, so auto-fill and the object's own position keep meaning what they did. */
  /* Outward, the outer edge is the opening plus the fillet's flare plus the
     border. Overlaid it is the WALL — the frame starts where the room's mouth
     is and works inward, so nothing overhangs the opening it covers, and the
     corner builder's rim lands the same way. */
  const ex = ovl ? hx : hx + bev + (B.left + B.right)/2;
  const ey = ovl ? hy : hy + bev + (B.top + B.bottom)/2;
  const cx = (B.left - B.right)/2;      // a fatter left border pushes the opening right
  const cy = (B.bottom - B.top)/2;      // a fatter top border pushes the opening down

  /* Where the frame's front face stops and its lip begins. */
  const lx = ovl ? hx - (B.left + B.right)/2 : hx + bev;
  const ly = ovl ? hy - (B.top + B.bottom)/2 : hy + bev;
  const mx = lx - bev, my = ly - bev;    // the lip's innermost extent, where the round ends
  /* Overlaid, the walls no longer slide with an uneven frame — the room is the
     whole screen and stays put; only the hole in front of it moves. */
  const wox = ovl ? 0 : cx, woy = ovl ? 0 : cy;

  // corners counter-clockwise seen from +Z: BL, BR, TR, TL
  const ring = (x,y,z,ox=0,oy=0)=>[[ox-x,oy-y,z],[ox+x,oy-y,z],[ox+x,oy+y,z],[ox-x,oy+y,z]];
  const R0 = ring(ex,   ey,   0);
  const R1 = ring(lx,   ly,   0,      cx,  cy);
  let R3 = ring(hx,     hy,   -depth, wox, woy);

  const aperture = R1;                       // the only opening
  const eps = Math.max(depth*2e-3, 1e-5);
  const ao = (q,n)=> aoStr<=0 ? null : formFactorToRect([q[0],q[1],Math.min(q[2],-eps)], n, aperture);

  const FWD = [0,0,1];
  // for side i (BL-BR, BR-TR, TR-TL, TL-BL): the axis pointing at the recess centre
  const INWARD = [[0,1,0],[-1,0,0],[0,-1,0],[1,0,0]];

  const lerp=(a,b,t)=>[a[0]+(b[0]-a[0])*t, a[1]+(b[1]-a[1])*t, a[2]+(b[2]-a[2])*t];
  const unit=v=>{const l=Math.hypot(v[0],v[1],v[2])||1; return [v[0]/l,v[1]/l,v[2]/l];};

  const pos=[], uv=[], col=[], nrm=[];
  const vtx=(v,n,shade)=>{
    pos.push(v[0],v[1],v[2]);
    nrm.push(n[0],n[1],n[2]);
    uv.push(v[0]/(2*ex)+.5, v[1]/(2*ey)+.5);
    const k = shade==null ? 1 : (1 - aoStr*(1-shade));
    col.push(k,k,k);
  };
  // each corner is [position, normal, occlusion]
  const quad=(A,B,C,D)=>{
    vtx(A[0],A[1],A[2]); vtx(B[0],B[1],B[2]); vtx(C[0],C[1],C[2]);
    vtx(A[0],A[1],A[2]); vtx(C[0],C[1],C[2]); vtx(D[0],D[1],D[2]);
  };

  /* Subdivided belt between two rings, one fixed normal per side. */
  const belt=(Ar,Br,nu,nv,n_,lit)=>{
    for(let i=0;i<4;i++){
      const j=(i+1)%4;
      const n = n_ ? n_(i) : FWD;
      const at=(u,v)=>lerp(lerp(Ar[i],Ar[j],u), lerp(Br[i],Br[j],u), v);
      for(let u=0;u<nu;u++) for(let v=0;v<nv;v++){
        const u0=u/nu,u1=(u+1)/nu, v0=v/nv,v1=(v+1)/nv;
        const c00=at(u0,v0), c10=at(u1,v0), c11=at(u1,v1), c01=at(u0,v1);
        quad([c00,n,lit?null:ao(c00,n)], [c10,n,lit?null:ao(c10,n)],
             [c11,n,lit?null:ao(c11,n)], [c01,n,lit?null:ao(c01,n)]);
      }
    }
  };

  /* Quarter-round from the border plane to the wall. The arc is centred at the
     corner the two planes would meet at if extended, so it lands tangent to
     each. One segment degenerates to a straight chamfer, which must stay flat-
     shaded — a smooth normal across a single flat facet would read as a fake
     round. */
  const bevelRings = [];
  for(let k=0;k<=bsegs;k++){
    const t = k/bsegs, th = t*Math.PI/2;
    const off = bsegs===1 ? bev*(1-t)      : bev*(1-Math.sin(th));
    const z   = bsegs===1 ? -bev*t         : -bev*(1-Math.cos(th));
    bevelRings.push({r: ring(mx+off, my+off, z, cx, cy), th});
  }
  const bevNormal = (i,th)=>{
    if(bsegs===1) return unit([INWARD[i][0]+FWD[0], INWARD[i][1]+FWD[1], 1]);
    const sn=Math.sin(th), cs=Math.cos(th);
    return [INWARD[i][0]*sn, INWARD[i][1]*sn, cs];
  };

  // Nothing occludes the outward-facing border, so it stays at full brightness.
  if(bor > 1e-6) belt(R0, R1, 1, 1, null, true);

  if(bev > 1e-6){
    const nu = 10;
    const nv = bsegs===1 ? 3 : 1;            // a chamfer still needs AO gradient
    for(let k=0;k<bsegs;k++){
      const A = bevelRings[k], B = bevelRings[k+1];
      for(let i=0;i<4;i++){
        const j=(i+1)%4;
        const nA = bevNormal(i, A.th), nB = bevNormal(i, B.th);
        const P=(u,v)=>lerp(lerp(A.r[i],A.r[j],u), lerp(B.r[i],B.r[j],u), v);
        for(let u=0;u<nu;u++) for(let v=0;v<nv;v++){
          const u0=u/nu,u1=(u+1)/nu, v0=v/nv,v1=(v+1)/nv;
          const n0=unit(lerp(nA,nB,v0)), n1=unit(lerp(nA,nB,v1));
          const c00=P(u0,v0), c10=P(u1,v0), c11=P(u1,v1), c01=P(u0,v1);
          quad([c00,n0,ao(c00,n0)], [c10,n0,ao(c10,n0)],
               [c11,n1,ao(c11,n1)], [c01,n1,ao(c01,n1)]);
        }
      }
    }
  }

  const R2 = bev > 1e-6 ? bevelRings[bsegs].r : R1;

  /* Where the walls begin. Outward this is R2 itself — the fillet ends exactly
     on the wall — but overlaid the wall stands OUTSIDE the lip, because the
     room is wider than the hole in front of it, and it starts at the back of
     the frame rather than at the back of the roll. */
  const WTOP = ring(hx, hy, ovl ? -fd : -bev, wox, woy);

  /* The straight part of the frame's inner face, below where the roll ends.
     Only exists once the border is deeper than its own bevel; at the default
     the two are equal and this is skipped rather than emitted flat. */
  const SKIRT = (ovl && fd > bev + 1e-9) ? ring(mx, my, -fd, cx, cy) : R2;
  if(SKIRT !== R2) belt(R2, SKIRT, 1, 8, i=>INWARD[i]);

  /* THE FLANGE UNDERSIDE. Overlaid, the frame is a plate with a rolled inner
     edge, and between where that edge ends and the wall it stands in front of
     there is a step. Left open, the frame terminates in mid-air and a grazing
     view looks straight under it; capped, it is a solid moulding from every
     angle. Degenerate outward (SKIRT and WTOP are the same ring), so it is
     skipped rather than emitted as zero-area triangles. */
  if(ovl && fd > 1e-6) belt(SKIRT, WTOP, 1, 1, ()=>[0,0,-1], true);

  /* The bevel is a rounded lip at the FRONT rim. A recess also has a back edge,
     where the side walls meet the back face, and leaving that sharp is what made
     the bevel look like it only touched the front. Mirror the same rounding there,
     as deep as the remaining depth allows, so one 'Bevel' value shapes both ends. */
  /* The straight wall has to survive between the frame's back and the back
     fillet, so it is the FRAME's depth this leaves room for, not the roll's —
     overlaid those are different numbers and a deep border ate the wall. */
  const bevB = Math.max(0, Math.min(bev, depth - (ovl ? fd : bev) - eps, Math.min(w,h)*0.49));

  if(bevB > 1e-6){
    // straight wall stops short, then a quarter-round turns into the back face
    const wallBottom = ring(hx, hy, -(depth - bevB), wox, woy);
    belt(WTOP, wallBottom, 10, 12, i=>INWARD[i]);

    const backRings = [];
    for(let k=0;k<=bsegs;k++){
      const t=k/bsegs, th=t*Math.PI/2;
      const roff = bsegs===1 ? bevB*t : bevB*(1-Math.cos(th));   // radius shrinks 0 -> bevB
      const zoff = bsegs===1 ? bevB*(1-t) : bevB*(1-Math.sin(th)); // z rises bevB -> 0
      backRings.push({ r: ring(hx-roff, hy-roff, -depth+zoff, wox, woy), th });
    }
    const backNormal = (i,th)=> bsegs===1
      ? unit([INWARD[i][0]+FWD[0], INWARD[i][1]+FWD[1], INWARD[i][2]+FWD[2]])
      : unit([INWARD[i][0]*Math.cos(th), INWARD[i][1]*Math.cos(th), Math.sin(th)]);

    const nu = 10;
    for(let k=0;k<bsegs;k++){
      const A = backRings[k], B = backRings[k+1];
      for(let i=0;i<4;i++){
        const j=(i+1)%4;
        const nA = backNormal(i, A.th), nB = backNormal(i, B.th);
        const P=(u)=>[lerp(A.r[i],A.r[j],u), lerp(B.r[i],B.r[j],u)];
        for(let u=0;u<nu;u++){
          const u0=u/nu,u1=(u+1)/nu;
          const [a0,b0]=P(u0), [a1,b1]=P(u1);
          quad([a0,nA,ao(a0,nA)], [a1,nA,ao(a1,nA)],
               [b1,nB,ao(b1,nB)], [b0,nB,ao(b0,nB)]);
        }
      }
    }
    R3 = backRings[bsegs].r;        // the back face is now the smaller, rounded rectangle
  } else {
    belt(WTOP, R3, 10, 16, i=>INWARD[i]);        // shallow recess: straight walls, sharp back
  }

  // back face
  {
    const n2 = 12;
    const at=(u,v)=>lerp(lerp(R3[0],R3[1],u), lerp(R3[3],R3[2],u), v);
    for(let u=0;u<n2;u++) for(let v=0;v<n2;v++){
      const u0=u/n2,u1=(u+1)/n2, v0=v/n2,v1=(v+1)/n2;
      const c00=at(u0,v0), c10=at(u1,v0), c11=at(u1,v1), c01=at(u0,v1);
      quad([c00,FWD,ao(c00,FWD)], [c10,FWD,ao(c10,FWD)],
           [c11,FWD,ao(c11,FWD)], [c01,FWD,ao(c01,FWD)]);
    }
  }

  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos,3));
  g.setAttribute('normal',   new THREE.Float32BufferAttribute(nrm,3));
  g.setAttribute('uv',       new THREE.Float32BufferAttribute(uv,2));
  g.setAttribute('color',    new THREE.Float32BufferAttribute(col,3));
  g.computeBoundingBox(); g.computeBoundingSphere();
  g.userData.tris = pos.length/9;
  return g;
}

/* A flat bezel: the outer rectangle with a rectangular hole cut out of it,
   borders adjustable per side via the same recessBorders() a recess uses.
   Genuinely flat (z=0 everywhere) rather than extruded — this is a thin
   plate, not a container, so there is no depth or bevel to speak of. When the
   borders meet or overlap in the middle there is no positive opening left to
   cut, and it degenerates gracefully into a solid rectangle rather than
   producing a self-intersecting hole. */
function buildFrameGeometry(p){
  const w = Math.max(.02, p.w), h = Math.max(.02, p.h);
  const hx = w/2, hy = h/2;
  const B = recessBorders(p);
  const ix0 = -hx + B.left, ix1 = hx - B.right;
  const iy0 = -hy + B.bottom, iy1 = hy - B.top;

  const shape = new THREE.Shape();
  shape.moveTo(-hx,-hy); shape.lineTo(hx,-hy); shape.lineTo(hx,hy); shape.lineTo(-hx,hy); shape.closePath();
  if(ix1 - ix0 > 1e-5 && iy1 - iy0 > 1e-5){
    const hole = new THREE.Path();
    hole.moveTo(ix0,iy0); hole.lineTo(ix1,iy0); hole.lineTo(ix1,iy1); hole.lineTo(ix0,iy1); hole.closePath();
    shape.holes.push(hole);
  }

  const g = new THREE.ShapeGeometry(shape);
  g.computeVertexNormals();
  g.computeBoundingSphere();
  g.userData.tris = triCount(g);
  return g;
}

/* A RECESS ACROSS A CORNER.

   For one flat screen a recess is a box sunk behind the plane, with one opening
   and a border around it. For a corner it is the same idea taken literally: a
   box NOTCHED OUT of the corner, with TWO of its six faces open — one aperture
   per panel, meeting along the shared edge.

     box:  x ∈ [W₁/2 − dL, W₁/2]      (dL = the opening's width on panel 1)
           z ∈ [−dR, 0]               (dR = the opening's width on panel 2)
           y ∈ [−h/2, +h/2]

   Six faces. `z = 0` is the aperture on the left panel and `x = W₁/2` the one
   on the right; both open. The other four — two back walls meeting at an
   internal vertical edge, plus a floor and a ceiling — are plain rectangles.
   Looking in, you see the inside corner of a room carved into the outside
   corner of a building, which is the effect the whole feature is for.

   The consequence worth knowing, because it removes a control: DEPTH IS NOT A
   FREE PARAMETER. What you see as depth through the left aperture *is* dR, the
   right aperture's width, and the other way round. There is no third number.

   Written beside buildRecessGeometry rather than folded into it. That builder
   is four-corner rings extruded back with a per-side INWARD table — a structure
   that assumes ONE aperture in ONE plane — and bending it would put every
   existing project's recess at risk to save a few dozen lines here. */
function buildCornerRecessGeometry(p){
  const hingeX = p.cornerHingeX, s = p.cornerConvex === false ? -1 : 1;
  const dL = Math.max(.01, p.cornerL), dR = Math.max(.01, p.cornerR);
  const hy = Math.max(.01, p.h) / 2;
  // panel 1 spans x up to the hinge; panel 2 runs from the hinge in −s·z
  const x0 = hingeX - dL, x1 = hingeX;
  const z0 = -s * dR,     z1 = 0;
  /* FOUR borders, not two. This used to read one X and one Y, which made the
     per-side controls lie on a corner: Top drove the bottom border as well, and
     Right drove nothing at all — it shrank the right APERTURE (cornerR) while
     the border beside it kept the left border's width, leaving an uncovered
     strip that read as black.

     cornerBorderX/Y are still honoured as the fallback so projects saved
     before this render exactly as they did. */
  /* Overlaid (see buildRecessGeometry's note), each member is spent out of the
     aperture it lies over, so it has to fit inside it. */
  const ovl = p.borderOverlay === true;
  const cap = (v, lim) => ovl ? Math.min(v, lim * .98) : v;
  const bxL = cap(Math.max(0, p.cornerBorderL ?? p.cornerBorderX ?? 0), dL);   // panel 1, far edge
  const bxR = cap(Math.max(0, p.cornerBorderR ?? p.cornerBorderX ?? 0), dR);   // panel 2, far edge
  const byT = cap(Math.max(0, p.cornerBorderT ?? p.cornerBorderY ?? 0), hy);
  const byB = cap(Math.max(0, p.cornerBorderB ?? p.cornerBorderY ?? 0), hy);

  /* The bevel, and where it deliberately is NOT. Each aperture gets a
     quarter-round along its three CLOSED edges — the far side, the top and the
     bottom — and none at the hinge, where the two apertures meet. A fillet
     there would draw a bright line straight down the corner, which is the one
     edge the whole illusion depends on reading as continuous.

     Clamped against the two aperture widths as well as the height, because on
     a corner those widths ARE the depths (see the note above): a bevel deeper
     than the notch would turn the geometry inside out. */
  /* BORDER DEPTH, as in the flat builder: null follows the bevel, so an unset
     value renders exactly as it always did. The notch's own depth is the two
     aperture widths here (see the note above), so the frame is held clear of
     the shallower of them rather than of a `depth` that does not exist. */
  const fd = ovl
    ? Math.min(p.borderDepth == null ? Math.max(0, p.bevel||0) : Math.max(0, p.borderDepth),
               Math.min(dL, dR) * .98)
    : 0;
  const bev = clamp(p.bevel || 0, 0, ovl
    ? Math.min(fd, Math.min(dL, dR, hy, dL - bxL, dR - bxR, hy - (byT + byB)/2) * .49)
    : Math.min(dL, dR, hy) * .49);
  const SEG = 5;                       // matches the flat recess's own roundness
  const zIn = -s;                      // which way "into the notch" runs in z

  const pos = [], nrm = [], uv = [], col = [];
  const span = Math.max(dL + dR, 2*hy);
  const vtx = (v, n) => {
    pos.push(v[0], v[1], v[2]);
    nrm.push(n[0], n[1], n[2]);
    // planar-ish uv: enough for a material to have something sensible to sample
    uv.push((v[0] - x0)/span + .5, (v[1] + hy)/span);
    col.push(1, 1, 1);
  };
  /* Wound so the listed order is counter-clockwise seen from the side `n`
     faces — the same convention the flat builder's own quad() keeps. */
  const quad = (a, b, c, d, n) => {
    /* WOUND TO AGREE WITH THE NORMAL IT WAS HANDED, rather than trusting four
       points to have been listed the right way round. They were not: panel 2's
       three border strips, and the wall seen through it, were all listed
       clockwise about their own +x normal.

       That is not invisible on a DoubleSide material — it is worse. Three flips
       the shading normal on a back-facing fragment, so those strips were lit as
       though they faced the opposite way and stayed dark whatever the lighting
       did. Reported as the second screen's border not receiving lights.

       Enforced here rather than corrected at each call site: this builder emits
       quads from five places including band(), the mirrored panel makes the
       right order genuinely hard to see by eye, and a rule that cannot be
       forgotten is worth more than four fixed orderings. A quad already listed
       correctly has a positive dot and is untouched. */
    const ux=b[0]-a[0], uy=b[1]-a[1], uz=b[2]-a[2];
    const vx=c[0]-a[0], vy=c[1]-a[1], vz=c[2]-a[2];
    const gx=uy*vz-uz*vy, gy=uz*vx-ux*vz, gz=ux*vy-uy*vx;
    if(gx*n[0] + gy*n[1] + gz*n[2] < 0){ const t = b; b = d; d = t; }
    vtx(a,n); vtx(b,n); vtx(c,n);
    vtx(a,n); vtx(c,n); vtx(d,n);
  };

  /* The bevel pushes the aperture's LIP outward by `bev` on its three closed
     sides and sets the interior back by `bev` along the opening's own axis, so
     the walls start where the fillet ends. With no bevel every term below is
     zero and the shape is the plain notch it was. */
  /* Overlaid there is no outward fillet to leave room for — the frame sits in
     front of the room rather than around its mouth — so the walls run the whole
     way to the screen plane and the frame's own cap lands on them. */
  const wz = z1 + (ovl ? 0 : zIn*bev); // where the left aperture's walls begin
  const wx = x1 - (ovl ? 0 : bev);     // where the right aperture's walls begin

  // ── the four interior faces, set back past the fillet ──────────────────
  const back1 = s > 0 ? [0,0,1] : [0,0,-1];      // the wall you see through panel 1
  quad([x0,-hy,z0], [x1,-hy,z0], [x1,hy,z0], [x0,hy,z0], back1);
  quad([x0,-hy,wz], [x0,hy,wz], [x0,hy,z0], [x0,-hy,z0], [1,0,0]);   // wall seen through panel 2
  quad([x0,hy,z0], [wx,hy,z0], [wx,hy,wz], [x0,hy,wz], [0,-1,0]);    // ceiling
  quad([x0,-hy,wz], [wx,-hy,wz], [wx,-hy,z0], [x0,-hy,z0], [0,1,0]); // floor

  /* ── the fillets ───────────────────────────────────────────────────────
     Swept as rings, the same way the flat recess does it: each ring is the
     aperture outline at one step of the quarter-round, offset outward by
     `off` and set into the notch by `depth`. Mitred at the two corners for
     free, because both offsets apply at once there. The hinge end of each
     ring stays put — that is the open side. */
  const arc = (i) => { const t = (i/SEG) * Math.PI/2;
    return { off: bev*(1 - Math.sin(t)), d: bev*(1 - Math.cos(t)), sn: Math.sin(t), cs: Math.cos(t) }; };
  if(!ovl && bev > 0){
    for(let i = 0; i < SEG; i++){
      const a = arc(i), b = arc(i+1);
      // left aperture: lip in the z = 0 plane, closed on x0, ±hy
      const La = { z: z1 + zIn*a.d, o: a.off }, Lb = { z: z1 + zIn*b.d, o: b.off };
      const nTop = (k) => [0, -k.sn, k.cs], nBot = (k) => [0, k.sn, k.cs], nOut = (k) => [k.sn, 0, k.cs];
      quad([x1, hy+La.o, La.z], [x0-La.o, hy+La.o, La.z],
           [x0-Lb.o, hy+Lb.o, Lb.z], [x1, hy+Lb.o, Lb.z], nTop(a));
      quad([x0-Lb.o, -hy-Lb.o, Lb.z], [x0-Lb.o, hy+Lb.o, Lb.z],
           [x0-La.o, hy+La.o, La.z], [x0-La.o, -hy-La.o, La.z], nOut(a));
      quad([x1, -hy-Lb.o, Lb.z], [x0-Lb.o, -hy-Lb.o, Lb.z],
           [x0-La.o, -hy-La.o, La.z], [x1, -hy-La.o, La.z], nBot(a));
      // right aperture: lip in the x = hinge plane, closed on z0, ±hy
      const Ra = { x: x1 - a.d, o: a.off }, Rb = { x: x1 - b.d, o: b.off };
      const mTop = (k) => [k.cs, -k.sn, 0], mBot = (k) => [k.cs, k.sn, 0], mOut = (k) => [k.cs, 0, -s*k.sn];
      quad([Ra.x, hy+Ra.o, z1], [Rb.x, hy+Rb.o, z1],
           [Rb.x, hy+Rb.o, z0+zIn*Rb.o], [Ra.x, hy+Ra.o, z0+zIn*Ra.o], mTop(a));
      quad([Ra.x, -hy-Ra.o, z0+zIn*Ra.o], [Rb.x, -hy-Rb.o, z0+zIn*Rb.o],
           [Rb.x, -hy-Rb.o, z1], [Ra.x, -hy-Ra.o, z1], mBot(a));
      quad([Ra.x, -hy-Ra.o, z0+zIn*Ra.o], [Ra.x, hy+Ra.o, z0+zIn*Ra.o],
           [Rb.x, hy+Rb.o, z0+zIn*Rb.o], [Rb.x, -hy-Rb.o, z0+zIn*Rb.o], mOut(a));
    }
  }

  /* ── the borders ───────────────────────────────────────────────────────
     Screen material left around each aperture: a strip on the far side and
     one above and below, starting where the bevel's outer lip ends. NONE at
     the hinge — that absence is the point, and it is what makes the two
     apertures read as one opening rather than as two insets that touch. */
  /* WHICH WAY EACH PANEL FACES. Panel 1 lies in z = z1 with the room receding
     at zIn = -s, so it faces +s in z. Panel 2 lies in x = x1 with the room at
     x < x1, so it faces +x WHATEVER s is — the same answer back1 and the
     x0 wall above already give, and the same one EI carries.

     The s used to sit on the other one of these two. Both are right at s = 1,
     which is why a convex corner only ever showed the winding fault above and
     a concave one had its borders facing into the building as well. */
  const FWD = [0,0,s], SIDE = [1,0,0];
  if(!ovl){
    const ax = x0 - bev, ay = hy + bev;      // the aperture's outer lip
    const az = z0 + zIn*bev;
    /* Each of the four is now its own strip. The two far-edge strips run the
       FULL height including the top and bottom borders, which is what covers the
       corners where they meet — so those are the quads that have to know byT and
       byB, not just their own width. */
    if(bxL > 0)                                        // panel 1's far edge
      quad([ax-bxL,-ay-byB,z1], [ax,-ay-byB,z1], [ax,ay+byT,z1], [ax-bxL,ay+byT,z1], FWD);
    if(bxR > 0){                                       // panel 2's far edge
      const zb = az - s*bxR;
      quad([x1,-ay-byB,zb], [x1,-ay-byB,az], [x1,ay+byT,az], [x1,ay+byT,zb], SIDE);
    }
    if(byT > 0){
      quad([ax,ay,z1], [x1,ay,z1], [x1,ay+byT,z1], [ax,ay+byT,z1], FWD);
      quad([x1,ay,az], [x1,ay,z1], [x1,ay+byT,z1], [x1,ay+byT,az], SIDE);
    }
    if(byB > 0){
      quad([ax,-ay-byB,z1], [x1,-ay-byB,z1], [x1,-ay,z1], [ax,-ay,z1], FWD);
      quad([x1,-ay-byB,az], [x1,-ay-byB,z1], [x1,-ay,z1], [x1,-ay,az], SIDE);
    }
  } else if(bxL > 0 || bxR > 0 || byT > 0 || byB > 0){
    /* ── ONLY BORDERS, ON A CORNER ─────────────────────────────────────────
       Swept as a closed SIX-POINT LOOP around the opening rather than
       assembled from strips, because a corner frame has to mitre in two
       different ways and a strip can do neither:

         · four ordinary frame corners, where two members of the same panel
           meet — the same 45° cut any picture frame has;
         · TWO HINGE CROSSINGS, where the top and bottom members wrap the
           convex edge and the direction "into the room" turns 90° with them.

       Both fall out of a single rule: a loop point takes every offset that
       applies to it. At a frame corner that is two inward offsets at once (the
       trick the outward fillets above already use). At a hinge it is the depth
       offset applied on BOTH axes — x1 - d and z1 + zIn*d — which is exactly
       the mitre, because the two panels are at right angles and each recedes
       along its own normal. Neither case is special-cased, so the bevel is
       continuous around all six and there is no seam at the corner, which is
       the one edge the illusion cannot afford to break.

       Still no member down the hinge itself: that absence is what makes the two
       apertures read as one opening, and it is why the loop has six points
       rather than eight. */
    const X0 = x0 + bxL, Z0 = z0 + s*bxR;    // inner edge of each far member
    const YT = hy - byT, YB = -hy + byB;     // inner edge of top and bottom
    const unit = v => { const l = Math.hypot(v[0],v[1],v[2]) || 1; return [v[0]/l, v[1]/l, v[2]/l]; };

    /* o — how far this ring has rolled inward off the frame's edge.
       d — how far it has sunk into the room.
       Points run bottom-far, hinge-bottom, far-p2-bottom, far-p2-top,
       hinge-top, top-far, so edge i lies between point i and point i+1 and
       each edge belongs to exactly one member. */
    const loop = (oL, oR, oT, oB, d) => {
      const X = X0 + oL, Z = Z0 + s*oR, T = YT - oT, Bm = YB + oB;
      const xh = x1 - d, zd = z1 + zIn*d;
      return [[X,Bm,zd], [xh,Bm,zd], [xh,Bm,Z], [xh,T,Z], [xh,T,zd], [X,T,zd]];
    };
    // per edge: the axis pointing into the opening, and the plane it faces out of
    const EI = [[0,1,0], [0,1,0], [0,0,s], [0,-1,0], [0,-1,0], [1,0,0]];
    const EF = [FWD, SIDE, SIDE, SIDE, FWD, FWD];
    const band = (A, Bq, nf) => {
      for(let i = 0; i < 6; i++){
        const j = (i+1) % 6;
        quad(A[i], A[j], Bq[j], Bq[i], nf(i));
      }
    };

    const rim  = loop(-bxL, -bxR, -byT, -byB, 0);   // where the frame meets the screen edge
    const lip  = loop(0, 0, 0, 0, 0);               // where its front face ends
    band(rim, lip, i => EF[i]);                     // the flat front face

    if(fd > 1e-6){
      /* o = bev·sinθ, d = bev·(1−cosθ): tangent to the front face where it
         leaves it and tangent to the depth axis where it arrives, so the round
         meets both without a crease. Normals are taken at the middle of each
         band — flat per band, as the outward fillets are, which at five bands
         is indistinguishable from smooth and keeps the two modes shading
         alike. */
      const prof = k => { const th = (k/SEG) * Math.PI/2;
        return { o: bev*Math.sin(th), d: bev*(1 - Math.cos(th)) }; };
      let prev = lip;
      /* Guarded, not merely degenerate: with no bevel every step of the profile
         collapses onto the lip, and running it anyway would emit five bands of
         zero-area triangles for a frame that is simply square-edged. */
      if(bev > 1e-6) for(let k = 0; k < SEG; k++){
        const b = prof(k+1);
        const next = loop(b.o, b.o, b.o, b.o, b.d);
        const th = ((k + .5)/SEG) * Math.PI/2, sn = Math.sin(th), cs = Math.cos(th);
        band(prev, next, i => unit([EI[i][0]*sn + EF[i][0]*cs,
                                    EI[i][1]*sn + EF[i][1]*cs,
                                    EI[i][2]*sn + EF[i][2]*cs]));
        prev = next;
      }
      /* The straight part of the inner face, once the border is deeper than its
         own roll. At the default the two are equal and this is skipped. */
      if(fd > bev + 1e-9){
        const skirt = loop(bev, bev, bev, bev, fd);
        band(prev, skirt, i => EI[i]);
        prev = skirt;
      }
      /* The underside. The frame's inner face ends one border-depth back, with
         the room's own wall standing further out still — leave that step open
         and the frame finishes in mid-air, so a grazing view looks straight
         under it. Capping it back out to the wall makes the frame a solid
         moulding from every angle, and lands its outer edge on the wall. */
      band(prev, loop(-bxL, -bxR, -byT, -byB, fd),
           i => [-EF[i][0], -EF[i][1], -EF[i][2]]);
    }
  }

  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos,3));
  g.setAttribute('normal',   new THREE.Float32BufferAttribute(nrm,3));
  g.setAttribute('uv',       new THREE.Float32BufferAttribute(uv,2));
  g.setAttribute('color',    new THREE.Float32BufferAttribute(col,3));

  /* SLIDE THE WHOLE NOTCH so an uneven top and bottom can exist at all.
     Everything above is built symmetric about y=0 — the aperture spans ±hy —
     so a taller top border could only ever be paid for by shrinking the
     opening at BOTH ends, which is why raising Top appeared to move the
     bottom with it. refitRecess already sizes h from the SUM of the two
     borders, which is correct; what was missing is where that opening sits.

     A rigid translate rather than an offset threaded through every vertex:
     with byT and byB independent the shape is already asymmetric, and shifting
     it by (byB - byT)/2 puts the OUTER edges back symmetric about the origin
     — which is what keeps the piece centred on the screen and auto-fill
     honest, exactly as the flat builder's own cx/cy do. */
  const cy = p.cornerOffsetY || 0;
  if(cy) g.translate(0, cy, 0);

  g.computeBoundingBox(); g.computeBoundingSphere();
  g.userData.tris = pos.length/9;
  return g;
}

export function buildGeometry(t,p){
  switch(t){
    /* A corner recess is the same object type with a different shape — the
       params carry the hinge, so nothing else has to know which it is. */
    case 'recess': return p.cornerHingeX != null
      ? buildCornerRecessGeometry(p) : buildRecessGeometry(p);
    case 'frame':  return buildFrameGeometry(p);
    case 'shape':  return buildShapeGeometry(p);
  }
  return new THREE.BoxGeometry(1,1,1);
}

/* A BASIC SHAPE from its sizes (DEF.shape, core/doc.js), centred on its own
   origin so the gizmo turns it about its middle. Upright shapes stand along +Y,
   and the torus and plane face the viewer (+Z), which is how each one is
   usually wanted in front of a screen. Segment counts are fixed and generous:
   these are seen large, on a billboard, where a faceted sphere shows. */
function buildShapeGeometry(p){
  const pos = v => Math.max(.001, +v || 0);
  const w = pos(p.w), h = pos(p.h), d = pos(p.d), r = pos(p.r), tube = pos(p.tube);
  let g;
  switch(p.shape){
    case 'sphere':   g = new THREE.SphereGeometry(r, 64, 32); break;
    case 'cylinder': g = roundedCylinder(r, h, +p.round || 0); break;
    case 'cone':     g = roundedCone(r, h, +p.round || 0); break;
    /* Four sides on a cone, turned 45° so the base is square rather than a
       diamond, then stretched to the width and depth asked for. */
    case 'pyramid':
      g = new THREE.ConeGeometry(Math.SQRT1_2, h, 4, 1);
      g.rotateY(Math.PI / 4);
      g.scale(w, 1, d);
      g.computeVertexNormals();
      break;
    case 'torus':    g = new THREE.TorusGeometry(r, Math.min(tube, r), 32, 96); break;
    /* h is the WHOLE height, caps included — the number a person means by
       "how tall" — where three's own argument is only the straight middle. */
    case 'capsule':  g = new THREE.CapsuleGeometry(r, Math.max(0, h - 2 * r), 16, 48); break;
    case 'plane':    g = new THREE.PlaneGeometry(w, h); break;
    default: {
      const rad = Math.min(Math.max(0, +p.round || 0), Math.min(w, h, d) / 2 - 1e-4);
      g = rad > 1e-4 ? new RoundedBoxGeometry(w, h, d, 6, rad) : new THREE.BoxGeometry(w, h, d);
    }
  }
  g.userData.tris = triCount(g);
  return g;
}

/* ROUNDED RIMS, by spinning a profile. A cylinder or a cone is a profile
   turned about its axis, so rounding its rim is a matter of drawing that
   profile with a quarter-circle where the edge was — exact, and tangent at
   both ends, so the shading runs smoothly off the flat cap and onto the side.
   The profile runs from the bottom centre, out along the base, round the rim,
   up the side and in to the top. A radius of 0 is the plain primitive, because
   a lathe would smooth the normals across a corner it cannot see is sharp. */
const LATHE = 64, ARC = 10;
function arc(pts, cx, cy, rad, from, to){
  for(let i = 0; i <= ARC; i++){
    const a = from + (to - from) * i / ARC;
    pts.push(new THREE.Vector2(Math.max(0, cx + rad * Math.cos(a)), cy + rad * Math.sin(a)));
  }
}
function roundedCylinder(r, h, round){
  const rr = Math.min(Math.max(0, round), r - 1e-4, h / 2 - 1e-4);
  if(rr <= 1e-4) return new THREE.CylinderGeometry(r, r, h, LATHE);
  const y0 = -h / 2, y1 = h / 2, pts = [new THREE.Vector2(0, y0)];
  arc(pts, r - rr, y0 + rr, rr, -Math.PI / 2, 0);        // bottom rim
  arc(pts, r - rr, y1 - rr, rr, 0, Math.PI / 2);         // top rim
  pts.push(new THREE.Vector2(0, y1));
  return new THREE.LatheGeometry(pts, LATHE);
}
function roundedCone(r, h, round){
  /* The fillet sits in the angle between the base and the slant. Its tangent
     points are t = radius / tan(half that angle) along each line from the
     corner, and t has to fit on both the base and the slant, which is what
     bounds the radius. The tip stays a point.

     Rounding a corner cuts it off, so a cone built on its own radius would
     come out narrower than the Radius field says — a lot narrower at a large
     rounding. So the sharp corner is placed further out, at R, chosen so the
     widest point of the rounded rim lands exactly on r: r = R - t + rr. R and
     t depend on each other through the slant's angle; a few rounds settle it. */
  let R = r, rr = 0, t = 0, k = 1;
  for(let i = 0; i < 6; i++){
    const L = Math.hypot(R, h);
    k = Math.tan(Math.acos(R / L) / 2);
    rr = Math.min(Math.max(0, round), .9 * Math.min(r, L) * k);
    t = rr / k;
    R = r + t - rr;
  }
  if(rr <= 1e-4) return new THREE.ConeGeometry(r, h, LATHE);
  const y0 = -h / 2;
  const pts = [new THREE.Vector2(0, y0)];
  arc(pts, R - t, y0 + rr, rr, -Math.PI / 2, Math.atan2(R, h));
  pts.push(new THREE.Vector2(0, h / 2));
  return new THREE.LatheGeometry(pts, LATHE);
}

/* Decoded textures are cached by their data URL. Rebuilds happen constantly —
   every material tweak, every slider drag — so decoding the image and uploading a
   fresh GPU texture each time would flicker and leak. One decode per unique image,
   shared across every material that references it, kept for the whole session.
   material.dispose() does not free an assigned map, so sharing is safe. */

/* Rasterise the string, trace it, and return real extruded geometry. */
/* Triangles, whether or not the geometry is indexed. ExtrudeGeometry is not and
   ShapeGeometry is, so reading position.count works for one and not the other. */
const triCount = g => (g.index ? g.index.count : g.attributes.position.count) / 3;

/* Split the copy into the lines that will actually be drawn, or null if there is
   nothing to draw. Shared so the geometry and the effects bitmap can never
   disagree about how many lines there are. */
function textLines(p){
  const raw = String(p.text == null ? '' : p.text).replace(/\r/g,'');
  const lines = raw.split('\n');
  while(lines.length > 1 && !lines[lines.length-1].trim()) lines.pop();   // stray trailing Enter
  while(lines.length > 1 && !lines[0].trim()) lines.shift();
  return lines.some(l=>l.trim()) ? lines : null;
}

/**
 * Lay the copy out on a canvas: how big it needs to be, where each baseline
 * sits, and the cap height in pixels that converts the whole thing to metres.
 *
 * `padExtra(cap, px)` returns additional padding in pixels. Anything that draws
 * OUTSIDE the glyphs — an outline stroke, a blurred shadow — has to say so here
 * or it gets clipped at the canvas edge, and the caller cannot compute it
 * itself because the sizes are percentages of a cap height only known in here.
 *
 * @returns {{px:number, cap:number, w:number, h:number, pad:number, lines:object[], font:string}|null}
 */
function layoutText(p, padExtra){
  const lines = textLines(p);
  if(!lines) return null;

  const align = p.align || 'center';
  const lhFactor = Math.max(.3, p.lineHeight == null ? 1.08 : p.lineHeight);
  const probe = document.createElement('canvas').getContext('2d');
  /* Everything goes through fontSpec so the quoting cannot be right in one place
     and wrong in another: `400 64px Playfair Display` is not a valid CSS font
     shorthand, and assigning an invalid string to ctx.font is a SILENT no-op —
     the canvas keeps its previous face and you trace the wrong family. */
  const font = n => fontSpec(p.font, p.weight, n);
  const setF = n => { probe.font = font(n); };

  /* Cap height is measured from a capital 'H', not from the user's own copy.
     Measuring the copy makes the letter size lurch whenever the words change —
     'POP' and 'pop' have very different ascents for the same font size. */
  const capAt = n => { setF(n); return probe.measureText('H').actualBoundingBoxAscent || n*.71; };

  /* Tracking is deliberately NOT the canvas `letterSpacing` property — browser
     support for it is uneven, and an unsupported value is a silent no-op,
     exactly the class of bug this codebase avoids everywhere else text is
     concerned. At tracking 0 (every existing project) a line is still one
     measureText/fillText call, unchanged; only a non-zero value pays for
     walking the string one glyph at a time. */
  const trackingPxAt = px => (p.tracking||0)/1000 * px;

  const layout = px => {
    setF(px);
    const cap = probe.measureText('H').actualBoundingBoxAscent || px*.71;
    const lh = lhFactor * cap;
    const trk = trackingPxAt(px);
    const L = lines.map(t=>{
      if(!t.length) return {t, ink:0, left:0, asc:0, desc:0, pieces:null};
      if(trk === 0){
        const m = probe.measureText(t);
        const left  = m.actualBoundingBoxLeft  || 0;
        const right = m.actualBoundingBoxRight || m.width;
        return {t, ink:left+right, left,
                asc:  m.actualBoundingBoxAscent  || px*.72,
                desc: m.actualBoundingBoxDescent || px*.22,
                pieces: null};                       // null = draw in one fillText call
      }
      /* One glyph at a time so the extra gap is real pixels, not a property the
         canvas is free to ignore. The trailing gap after the last glyph is left
         out of `ink` on purpose — including it would optically shift centred or
         right-aligned tracked text by an invisible amount. */
      const chars = [...t];
      let x = 0, left = 0, right = 0, asc = 0, desc = 0;
      const pieces = chars.map((ch,i)=>{
        const m = probe.measureText(ch);
        if(i === 0) left = m.actualBoundingBoxLeft || 0;
        const adv = m.width;
        if(i === chars.length-1) right = x + (m.actualBoundingBoxRight || adv);
        asc  = Math.max(asc,  m.actualBoundingBoxAscent  || px*.72);
        desc = Math.max(desc, m.actualBoundingBoxDescent || px*.22);
        const piece = {text:ch, dx:x};
        x += adv + trk;
        return piece;
      });
      return {t, ink:left+right, left, asc, desc, pieces};
    });
    const maxInk = Math.max(1, ...L.map(l=>l.ink));
    // vertical extent relative to the first line's baseline
    let top = 0, bottom = 0;
    L.forEach((l,i)=>{
      const base = i*lh;
      top    = Math.min(top,    base - (l.asc  || cap));
      bottom = Math.max(bottom, base + (l.desc || 0));
    });
    const pad = Math.ceil(px*.12) + 2 + Math.ceil(padExtra ? padExtra(cap, px) : 0);
    const w = Math.ceil(maxInk + pad*2);
    const h = Math.ceil((bottom - top) + pad*2);
    L.forEach((l,i)=>{
      const slack = maxInk - l.ink;
      const dx = align==='left' ? 0 : align==='right' ? slack : slack/2;
      l.x = pad + dx + l.left;
      l.y = pad - top + i*lh;
    });
    return {px, cap, w, h, pad, lines:L, font:font(px)};
  };

  const D = TEXT_DETAIL[p.detail] || TEXT_DETAIL.normal;
  let px = Math.max(24, D.cap / (capAt(100)/100));
  let LO = layout(px);

  /* Every extra line is another canvas-height of cells to walk. Shrink the
     resolution to stay inside budget rather than bailing out — a five-line block
     should get coarser, not vanish. */
  const BUDGET = 8e6;
  if(LO.w*LO.h > BUDGET){
    px = Math.max(24, px * Math.sqrt(BUDGET/(LO.w*LO.h)));
    LO = layout(px);
  }
  if(!(LO.w>2 && LO.h>2) || LO.w*LO.h > BUDGET*1.35) return null;
  return LO;
}

/**
 * @param {object} p                text params
 * @param {object} [opts]
 * @param {number} [opts.dilatePct] grow the silhouette by this % of cap height,
 *                                  which is how the outline shell is built
 * @param {boolean} [opts.flat]     ShapeGeometry instead of ExtrudeGeometry — 2D mode
 */
export function buildTextGeometry(p, opts = {}){
  const D = TEXT_DETAIL[p.detail] || TEXT_DETAIL.normal;
  const dilatePct = Math.max(0, opts.dilatePct || 0);

  const LO = layoutText(p, cap => cap * dilatePct/100);
  if(!LO) return null;
  const dilate = LO.cap * dilatePct/100;

  const w = LO.w, h = LO.h;

  const cv = document.createElement('canvas');
  cv.width = w; cv.height = h;
  const c2 = cv.getContext('2d');                     // resizing reset the state
  c2.font = LO.font;
  c2.textBaseline = 'alphabetic';
  c2.fillStyle = '#fff';
  /* Stroking as well as filling grows the silhouette outward by half the line
     width, and what gets traced is the union of the two. That is the outline
     shell: canvas does the polygon offset exactly, including around counters,
     where offsetting the traced contours by hand would have to work out which
     loops are holes and shrink those instead. */
  if(dilate > 0){
    c2.strokeStyle = '#fff';
    c2.lineJoin = 'round';
    c2.lineCap = 'round';
    c2.lineWidth = dilate*2;
  }
  /* `pieces` is only set when tracking is on — one glyph at a time, each at its
     own x offset. Otherwise the whole line draws in a single call, same as
     before tracking existed. */
  for(const l of LO.lines){
    if(!l.t.length) continue;
    if(l.pieces){
      for(const pc of l.pieces){
        const x = l.x + pc.dx;
        if(dilate > 0) c2.strokeText(pc.text, x, l.y);
        c2.fillText(pc.text, x, l.y);
      }
    }else{
      if(dilate > 0) c2.strokeText(l.t, l.x, l.y);
      c2.fillText(l.t, l.x, l.y);
    }
  }

  const img = c2.getImageData(0,0,w,h).data;
  const alpha = new Float32Array(w*h);
  let ink = 0;
  for(let i=0,j=3;i<alpha.length;i++,j+=4){ const a = img[j]/255; alpha[i]=a; if(a>.5) ink++; }
  if(ink < 4) return null;

  let loops = traceIso(alpha, w, h, .5).map(l=>simplifyLoop(l, D.tol)).filter(l=>l.length>=3);
  if(!loops.length) return null;
  // drop specks left by antialiasing on hairline serifs
  const biggest = Math.max(...loops.map(l=>Math.abs(loopArea(l))));
  loops = loops.filter(l=>Math.abs(loopArea(l)) > biggest*2e-4);

  const scale = Math.max(.01, p.capH) / LO.cap;      // one cap height == capH metres
  for(const l of loops) for(const q of l){ q[0] = q[0]*scale; q[1] = -q[1]*scale; }

  const shapes = loopsToShapes(loops);
  if(!shapes.length) return null;

  /* 2D mode: the same traced shapes, filled rather than extruded. No bevel and
     no creaseNormals — a flat face has one normal and nothing to smooth. This is
     the same treatment core/mask.js gives a masked image plane. */
  if(opts.flat){
    const flat = new THREE.ShapeGeometry(shapes);
    flat.computeBoundingBox();
    const fc = flat.boundingBox.getCenter(new THREE.Vector3());
    flat.translate(-fc.x, -fc.y, -fc.z);
    flat.computeVertexNormals();
    flat.computeBoundingSphere();
    /* ShapeGeometry is INDEXED, unlike ExtrudeGeometry — position.count is the
       vertex count, not three per triangle, and reading it as one gives a
       fractional triangle count in the inspector. */
    flat.userData.tris = triCount(flat);
    flat.userData.clampedBevel = false;
    return flat;
  }

  const depth = Math.max(.001, p.depth);
  const bevMax = Math.min(depth*.45, p.capH*.09);
  const bev = clamp(p.bevel==null?0:p.bevel, 0, bevMax);
  /* ExtrudeGeometry grows the bevel OUTSIDE `depth`, spanning -bev … depth+bev.
     Shrink the core so the total is exactly the depth the user asked for. */
  const core = Math.max(5e-4, depth - 2*bev);
  const geo = new THREE.ExtrudeGeometry(shapes, {
    depth: core, steps:1, curveSegments:1,
    bevelEnabled: bev > 1e-5,
    bevelThickness: bev, bevelSize: bev,
    bevelOffset: 0,
    bevelSegments: bev > 1e-5 ? 5 : 1   // bevel shape is no longer a user choice — always "very rounded"
  });
  geo.computeBoundingBox();
  const c = geo.boundingBox.getCenter(new THREE.Vector3());
  geo.translate(-c.x, -c.y, -c.z);                    // origin at the text centre
  creaseNormals(geo, p.smooth==null ? 35 : p.smooth);
  geo.computeBoundingSphere();
  geo.userData.tris = triCount(geo);
  geo.userData.clampedBevel = (p.bevel||0) > bevMax + 1e-9;
  return geo;
}

/* ── shadow and glow ─────────────────────────────────────────────────────────
   These two are the effects that are DEFINED by their blur, and blur is not
   something geometry can do — so unlike the outline they are painted into a
   canvas and hung on a plane behind the type. Canvas gives exactly the Gaussian
   falloff a design tool does, for free, at whatever resolution the layout picked.

   Both are drawn here rather than in two passes because they share a canvas: one
   texture, one plane, one draw call, whether you have enabled one or both. */

const FX_MAX_PX = 4096;    // texture ceiling — a huge glow must not blow up VRAM

/**
 * @returns {{canvas:HTMLCanvasElement, w:number, h:number}|null}
 *          w/h are the plane size IN METRES; null when neither effect is on.
 */
/**
 * The same two effects, for an object that is a PICTURE rather than type.
 *
 * buildTextEffects paints glyphs, and a picture has none — so what casts here is
 * the object's own SILHOUETTE, built once into a small canvas and then blurred
 * through the identical off-canvas shadow idiom. Three sources of silhouette,
 * in the order they are honoured:
 *
 *   · the mask polygon, if the object is masked — otherwise a cut-out would
 *     glow as the rectangle it was cut from, which is the one thing a mask
 *     exists to stop;
 *   · the picture's own alpha, which is what makes a transparent PNG throw a
 *     halo shaped like the thing in it rather than like its file;
 *   · failing both, the plain rectangle, which is a real drop shadow on a
 *     photograph and the only honest answer for a clip.
 *
 * Sizes are percentages of the object's HEIGHT — cap height is what they mean
 * for type, and this is the nearest thing a picture has, so the same numbers
 * give the same halo on both.
 *
 * @param {object} P      the object's params, for its fx* block
 * @param {CanvasImageSource|null} source  the decoded picture, or null for shape only
 * @param {number} wM     plane width in metres
 * @param {number} hM     plane height in metres
 * @param {object|null} mask  o.mask, honoured as above
 * @returns {{canvas:HTMLCanvasElement, w:number, h:number}|null}
 */
export function buildBitmapEffects(P, source, wM, hM, mask){
  const shadowOn = !!P.fxShadow, glowOn = !!P.fxGlow, outlineOn = !!P.fxOutline;
  if(!shadowOn && !glowOn && !outlineOn) return null;
  if(!(wM > 0) || !(hM > 0)) return null;

  const pct = v => Math.max(0, +v || 0) / 100;
  const sBlur = shadowOn ? pct(P.fxShadowBlur) : 0;
  const sDX   = shadowOn ? (+P.fxShadowDX || 0)/100 : 0;
  const sDY   = shadowOn ? (+P.fxShadowDY || 0)/100 : 0;
  const gSize = glowOn ? pct(P.fxGlowSize) : 0;
  const oW    = outlineOn ? pct(P.fxOutlineW == null ? 6 : P.fxOutlineW) : 0;

  /* Everything here reaches outside the picture, so the canvas grows by the
     furthest any of it travels — the same 3x rule buildTextEffects uses for a
     blur's visible falloff. */
  const padU = Math.max(sBlur*3 + Math.max(Math.abs(sDX), Math.abs(sDY)), gSize*3, oW);
  const padM = hM * padU;
  if(!(padM > 0)) return null;              // every effect on, all of them at zero

  /* Resolution follows the source when there is one, so a big PNG gets a halo
     as sharp as itself and a small one does not pay for pixels it has no detail
     to put in. Then scaled down as a whole if the padded result would breach
     the texture ceiling, rather than refusing to build. */
  const natW = source ? (source.naturalWidth || source.videoWidth || source.width || 0) : 0;
  const basePx = Math.max(64, Math.min(2048, natW || 1024));
  let PPM = basePx / wM;
  const spanPx = () => Math.max((wM + 2*padM) * PPM, (hM + 2*padM) * PPM);
  if(spanPx() > FX_MAX_PX) PPM *= FX_MAX_PX / spanPx();

  const KW = Math.max(1, Math.round(wM * PPM));
  const KH = Math.max(1, Math.round(hM * PPM));
  const KP = Math.max(1, Math.round(padM * PPM));

  /* The silhouette, drawn once. Every effect below is this shape blurred and
     coloured, which is what keeps them agreeing with each other. */
  const sil = document.createElement('canvas');
  sil.width = KW; sil.height = KH;
  const s2 = sil.getContext('2d');
  const pts = mask && mask.on && mask.points && mask.points.length >= 3 ? mask.points : null;
  if(pts){
    /* Mask points are normalised with v running DOWN the picture (see
       core/mask.js's toXY), which is already canvas order — no flip. */
    s2.beginPath();
    s2.moveTo(pts[0][0]*KW, pts[0][1]*KH);
    for(let i=1;i<pts.length;i++) s2.lineTo(pts[i][0]*KW, pts[i][1]*KH);
    s2.closePath();
    if(mask.invert){ s2.rect(0, 0, KW, KH); s2.clip('evenodd'); }   // the rect MINUS the shape
    else s2.clip();
  }
  if(source) s2.drawImage(source, 0, 0, KW, KH);
  else { s2.fillStyle = '#fff'; s2.fillRect(0, 0, KW, KH); }

  const cv = document.createElement('canvas');
  cv.width = KW + KP*2; cv.height = KH + KP*2;
  const c2 = cv.getContext('2d');

  /* Drawing the silhouette with a shadow set also draws the silhouette itself,
     which reads as a solid duplicate rather than a shadow. Same fix as the type
     path: push it far off-canvas and give the shadow an equal and opposite
     offset, so only the shadow lands in frame. */
  const OFF = cv.width + cv.height + 1000;
  const paint = (color, blur, dx, dy, alpha, passes) => {
    c2.save();
    c2.globalAlpha = alpha;
    c2.shadowColor = color;
    c2.shadowBlur = blur;
    for(let i=0;i<passes;i++){
      c2.shadowOffsetX = dx + OFF;
      c2.shadowOffsetY = dy;
      c2.drawImage(sil, KP - OFF, KP);
    }
    c2.restore();
  };

  // Glow, then shadow over it, then the outline nearest the picture — the same
  // back-to-front order the three layers of a text object are stacked in.
  if(glowOn && gSize > 0)
    paint(P.fxGlowColor || '#F2C300', hM*gSize*PPM, 0, 0,
          clamp(P.fxGlowOpacity == null ? .8 : +P.fxGlowOpacity, 0, 1), 3);

  // canvas Y runs down and the scene's runs up, hence the negated dy
  if(shadowOn)
    paint(P.fxShadowColor || '#000000', hM*sBlur*PPM, hM*sDX*PPM, -hM*sDY*PPM,
          clamp(P.fxShadowOpacity == null ? .55 : +P.fxShadowOpacity, 0, 1), 1);

  /* No traced contour to stroke, so the rim is the silhouette stamped around a
     circle — the standard bitmap dilate. The count follows the rim's own length
     rather than being fixed: a stamp roughly every two pixels, which is where
     the scallops between them stop being visible. A fixed sixteen looked clean
     on a thin rim and beaded on a thick one, since the gaps grow with radius. */
  if(outlineOn && oW > 0){
    const r = hM*oW*PPM;
    const N = Math.max(16, Math.min(64, Math.round(Math.PI*r)));
    for(let i=0;i<N;i++){
      const a = i/N * Math.PI*2;
      paint(P.fxOutlineColor || '#000000', 0, Math.cos(a)*r, Math.sin(a)*r, 1, 1);
    }
  }

  return { canvas: cv, w: cv.width/PPM, h: cv.height/PPM };
}

export function buildTextEffects(p){
  const shadowOn = !!p.fxShadow, glowOn = !!p.fxGlow;
  if(!shadowOn && !glowOn) return null;

  const pct = v => Math.max(0, +v || 0) / 100;
  const sBlur = shadowOn ? pct(p.fxShadowBlur) : 0;
  const sDX   = shadowOn ? (+p.fxShadowDX || 0)/100 : 0;
  const sDY   = shadowOn ? (+p.fxShadowDY || 0)/100 : 0;
  const gSize = glowOn ? pct(p.fxGlowSize) : 0;

  /* Everything here reaches outside the glyphs, so the canvas has to be grown by
     the furthest any of it travels or the effect is sliced off at the edge.
     A blur's visible falloff runs to roughly 3x its radius. */
  const padPct = Math.max(sBlur*3 + Math.max(Math.abs(sDX), Math.abs(sDY)), gSize*3);
  const LO = layoutText(p, cap => cap * padPct);
  if(!LO) return null;

  const cv = document.createElement('canvas');
  const over = Math.max(LO.w, LO.h) / FX_MAX_PX;
  const k = over > 1 ? 1/over : 1;                    // downscale rather than refuse
  cv.width = Math.max(2, Math.round(LO.w*k));
  cv.height = Math.max(2, Math.round(LO.h*k));

  const c2 = cv.getContext('2d');
  c2.scale(k, k);
  c2.font = LO.font;
  c2.textBaseline = 'alphabetic';

  const cap = LO.cap;
  const paint = (color, blur, dx, dy, alpha, passes) => {
    /* Drawing the glyph with a shadow set also paints the glyph, which reads as
       a solid duplicate rather than a shadow. The fix is the standard canvas
       idiom: push the glyph far off-canvas and give the shadow an equal and
       opposite offset, so only the shadow lands in frame. */
    const OFF = LO.w + LO.h + 1000;
    c2.save();
    c2.globalAlpha = alpha;
    c2.fillStyle = '#000';                            // never seen; the shadow carries the colour
    c2.shadowColor = color;
    c2.shadowBlur = blur;
    for(let i=0;i<passes;i++){
      c2.shadowOffsetX = dx + OFF;
      c2.shadowOffsetY = dy;
      for(const l of LO.lines) if(l.t.length){
        if(l.pieces) for(const pc of l.pieces) c2.fillText(pc.text, (l.x + pc.dx) - OFF, l.y);
        else c2.fillText(l.t, l.x - OFF, l.y);
      }
    }
    c2.restore();
  };

  /* Glow first so the shadow sits over it — a shadow is meant to read as the
     furthest thing back, and an outer glow hugs the letters. One pass of blur is
     always too faint to see against a lit scene, so it is built up. */
  if(glowOn && gSize > 0)
    paint(p.fxGlowColor || '#F2C300', cap*gSize, 0, 0,
          clamp(p.fxGlowOpacity == null ? .8 : +p.fxGlowOpacity, 0, 1), 3);

  if(shadowOn)
    paint(p.fxShadowColor || '#000000', cap*sBlur, cap*sDX, -cap*sDY,
          clamp(p.fxShadowOpacity == null ? .55 : +p.fxShadowOpacity, 0, 1), 1);

  // canvas Y runs down and the scene's runs up, hence the negated shadow dy above
  const scale = Math.max(.01, p.capH) / cap;
  return { canvas: cv, w: LO.w*scale, h: LO.h*scale };
}
